import React, { useState, useEffect } from 'react';
import { Card, List, Button, Input, message, Tag, Space, Empty, Spin } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getSidebarInfo,
  quickFollowUp,
  updateCaseStatus,
  SidebarCaseInfo,
  SidebarInfo,
} from '../../services/wechatSidebar';
import './styles.css';

const { TextArea } = Input;

const WechatSidebar: React.FC = () => {
  const [info, setInfo] = useState<SidebarInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState<SidebarCaseInfo | null>(null);
  const [followUpContent, setFollowUpContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  // Get group_id from URL query parameter
  const getGroupId = () => {
    const params = new URLSearchParams(window.location.search);
    const groupId = params.get('group_id') || '';
    
    setDebugInfo({
      url: window.location.href,
      search: window.location.search,
      groupId: groupId,
      allParams: Object.fromEntries(params.entries()),
      timestamp: new Date().toISOString()
    });
    
    return groupId;
  };

  const loadData = async () => {
    const groupId = getGroupId();
    if (!groupId) {
      message.error('缺少群聊ID参数 (group_id)');
      console.error('URL中没有group_id参数');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getSidebarInfo(groupId);
      setInfo(data);
    } catch (error: any) {
      console.error('加载侧边栏数据失败:', error);
      console.error('错误详情:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 404) {
        message.error(`群聊不存在 (group_id: ${groupId})`);
      } else if (error.response?.status === 401) {
        message.error('未登录或登录已过期');
      } else {
        message.error('加载失败: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleQuickFollowUp = async () => {
    if (!selectedCase || !followUpContent.trim()) {
      message.warning('请输入跟进内容');
      return;
    }

    setSubmitting(true);
    try {
      await quickFollowUp({
        case_id: selectedCase.id,
        content: followUpContent,
        follow_up_type: 'communication',
      });
      message.success('跟进记录已添加');
      setFollowUpContent('');
      setSelectedCase(null);
    } catch (error) {
      console.error('Failed to add follow-up:', error);
      message.error('添加跟进失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (caseItem: SidebarCaseInfo, newStatus: string) => {
    try {
      await updateCaseStatus({
        case_id: caseItem.id,
        case_status: newStatus,
      });
      message.success('状态已更新');
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
      message.error('更新状态失败');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'blue',
      closed: 'default',
      suspended: 'orange',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      active: '进行中',
      closed: '已结案',
      suspended: '暂停',
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="wechat-sidebar-loading">
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#666' }}>加载中...</div>
        {debugInfo && (
          <div style={{ 
            marginTop: 16, 
            padding: 12, 
            background: '#f5f5f5', 
            borderRadius: 4,
            fontSize: 12,
            textAlign: 'left',
            maxWidth: 400
          }}>
            <div><strong>调试信息：</strong></div>
            <div>URL: {debugInfo.url}</div>
            <div>group_id: {debugInfo.groupId || '(未获取到)'}</div>
          </div>
        )}
      </div>
    );
  }

  if (!info) {
    return (
      <div className="wechat-sidebar-empty">
        <Empty description="加载失败" />
        {debugInfo && (
          <div style={{ 
            marginTop: 16, 
            padding: 12, 
            background: '#fff3cd', 
            borderRadius: 4,
            fontSize: 12,
            textAlign: 'left',
            maxWidth: 500,
            border: '1px solid #ffc107'
          }}>
            <div><strong>🔍 调试信息：</strong></div>
            <div>完整URL: {debugInfo.url}</div>
            <div>group_id: {debugInfo.groupId || '❌ 未获取到'}</div>
            <div>所有参数: {JSON.stringify(debugInfo.allParams)}</div>
            <div style={{ marginTop: 8, color: '#856404' }}>
              💡 提示：企业微信应该自动在URL中添加 group_id 参数
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wechat-sidebar">
      <div className="sidebar-header">
        <h3>{info.group.group_name}</h3>
        <p className="group-info">
          {info.group.member_count && `${info.group.member_count}人`} · {info.total_cases}个案件
        </p>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadData}
          size="small"
          style={{ marginTop: 8 }}
        >
          刷新
        </Button>
      </div>

      <div className="sidebar-content">
        {info.cases.length === 0 ? (
          <Empty description="暂无关联案件" />
        ) : (
          <List
            dataSource={info.cases}
            renderItem={(caseItem) => (
              <Card
                size="small"
                className="case-card"
                hoverable
                onClick={() => setSelectedCase(caseItem)}
              >
                <div className="case-header">
                  <span className="case-number">{caseItem.case_number}</span>
                  <Tag color={getStatusColor(caseItem.case_status)}>
                    {getStatusText(caseItem.case_status)}
                  </Tag>
                </div>
                <div className="case-name">{caseItem.case_name}</div>
                {caseItem.case_type && (
                  <div className="case-meta">类型：{caseItem.case_type}</div>
                )}
                {caseItem.responsible_lawyer_name && (
                  <div className="case-meta">负责人：{caseItem.responsible_lawyer_name}</div>
                )}
                {caseItem.accept_date && (
                  <div className="case-meta">承接日期：{caseItem.accept_date}</div>
                )}
              </Card>
            )}
          />
        )}
      </div>

      {selectedCase && (
        <div className="quick-follow-up">
          <div className="follow-up-header">
            <h4>快速跟进：{selectedCase.case_name}</h4>
            <Button size="small" onClick={() => setSelectedCase(null)}>
              取消
            </Button>
          </div>
          <TextArea
            value={followUpContent}
            onChange={(e) => setFollowUpContent(e.target.value)}
            placeholder="输入跟进内容..."
            rows={4}
            maxLength={500}
          />
          <div className="follow-up-actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleQuickFollowUp}
              loading={submitting}
              disabled={!followUpContent.trim()}
            >
              添加跟进
            </Button>
            <Space>
              <Button
                size="small"
                onClick={() => handleStatusChange(selectedCase, 'active')}
              >
                进行中
              </Button>
              <Button
                size="small"
                onClick={() => handleStatusChange(selectedCase, 'suspended')}
              >
                暂停
              </Button>
              <Button
                size="small"
                onClick={() => handleStatusChange(selectedCase, 'closed')}
              >
                结案
              </Button>
            </Space>
          </div>
        </div>
      )}
    </div>
  );
};

export default WechatSidebar;
